import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import { sessionEnd, showSnackbar } from "../../redux/actions";

// CORRECTION : Ajout du "x-" dans le nom du package
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridRenderCellParams, // Pour le typage
} from "@mui/x-data-grid";

import { 
  Box, Card, Alert, Button, IconButton, 
  InputBase, Tooltip, Chip, styled,
  Typography 
} from "@mui/material";

// Icons
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddIcon from '@mui/icons-material/Add';

// Fetchs & Composants
import { getAll, getWithFilter } from "../../fetchs/get";
import { deleteOne } from "../../fetchs/delete";
import { fetchPostProtect } from "../../fetchs/post";
import { IHosting } from "../../interface/hosting"; 
import { useNavigate } from "react-router-dom";
import AlerteDialog from "../../components/AlerteDialog/AlerteDialog";
import CustomNoRowsOverlay from "../../components/CustomNoRowsOverlay";
import NavTab from "../../layout/NavTab";

const SearchWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "12px",
  backgroundColor: "#fff",
  border: "1px solid #e2e8f0",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
  },
  marginLeft: theme.spacing(2),
  width: "300px",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  color: "#94a3b8",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "#1e293b",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.2, 1, 1.2, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    fontSize: "0.9rem",
  },
}));

const ListHostings = () => {
  const [hostings, setHostings] = useState<IHosting[]>([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [openConfirmCopy, setOpenConfirmCopy] = useState(false);
  const [hostingId, setHostingId] = useState<string | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "createdAt", sort: "desc" }]);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.user.token);

  const getAllHostings = () => {
    if (token) {
      getAll(token, "/hosting/", setHostings, () => dispatch(sessionEnd()), setError);
    }
  };

  useEffect(() => { getAllHostings(); }, []);

  const handleAction = (id: string, action: 'delete' | 'copy') => {
    setHostingId(id);
    action === 'delete' ? setOpen(true) : setOpenConfirmCopy(true);
  };

  // CORRECTION : Alignement sur les arguments de deleteOne
  const handleDelete = () => {
    if (token && hostingId) {
      deleteOne(token, "/hosting/", hostingId).then((res: any) => {
        if (res.status === 200) {
          dispatch(showSnackbar("Hébergement supprimé avec succès", "success"));
          setOpen(false);
          getAllHostings();
        } else {
          dispatch(showSnackbar("Erreur lors de la suppression", "error"));
        }
      }).catch((err) => setError(err));
    }
  };

  const handleDuplicate = () => {
    // On trouve l'objet complet pour récupérer son nom
    const hostingToCopy = hostings.find(h => h._id === hostingId);
    
    if (token && hostingToCopy) {
      // Préparation du corps de la requête
      // On envoie l'ID d'origine et le nouveau nom
      const body = { 
        _id: hostingToCopy._id, 
        name: `${hostingToCopy.name} (Copie)` 
      };

      // CORRECTION : Utilisation de la route /duplicate au lieu de /add
      fetchPostProtect(token, "/hosting/duplicate", JSON.stringify(body))
        .then((res) => {
          if (res.status === 200) {
            dispatch(showSnackbar("Hébergement dupliqué avec succès", "success"));
            setOpenConfirmCopy(false);
            getAllHostings(); // Rafraîchir la liste
          } else {
            // Optionnel : essayer de lire l'erreur du serveur
            res.json().then((data) => {
              dispatch(showSnackbar(`Erreur : ${data.error || "Duplication échouée"}`, "error"));
            }).catch(() => {
              dispatch(showSnackbar("Erreur lors de la duplication", "error"));
            });
          }
        })
        .catch((err) => {
          setError(err.toString());
          dispatch(showSnackbar("Erreur réseau", "error"));
        });
    }
  };

  // CORRECTION : Typage GridColDef et params
  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Hébergement",
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.875rem" }}>
          {params.value}
        </Typography>
      ),
    },
    { field: "client", headerName: "Client", flex: 1.5 },
    { field: "type", headerName: "Type", flex: 1.2, renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value} 
          size="small" 
          sx={{ 
            fontWeight: 700, 
            bgcolor: params.value === "Classique" ? "#e0f2fe" : "#fef3c7",
            color: params.value === "Classique" ? "#0369a1" : "#92400e"
          }} 
        />
      )
    },
    { field: "author", headerName: "Auteur", flex: 1.2 },
    {
      field: "createdAt",
      headerName: "Création",
      width: 130,
      type: "date",
      valueGetter: (params) => {
        if (!params.row.createdAt) return null;
        return new Date(params.row.createdAt.split("/").reverse().join("-"));
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Ouvrir">
            <IconButton size="small" onClick={() => navigate(`/hebergement/${params.id}`)}>
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Dupliquer">
            <IconButton size="small" onClick={() => handleAction(params.id as string, 'copy')} sx={{ color: "#f59e0b" }}>
              <ContentCopyTwoToneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton size="small" onClick={() => handleAction(params.id as string, 'delete')} sx={{ color: "#ef4444" }}>
              <DeleteTwoToneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = hostings.map((h: IHosting) => ({
    id: h._id,
    name: h.name,
    client: h.client ? h.client.name : "N/C",
    type: h.type === "classic" ? "Classique" : "Personnalisé",
    author: h.author,
    createdAt: h.createdAt,
    updatedAt: h.updatedAt,
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <NavTab text="Liste des hébergements" />

      <Card elevation={0} sx={{ mx: 3, mb: 3, p: 2, borderRadius: "16px", border: "1px solid #e2e8f0", flexGrow: 1, display: "flex", flexDirection: "column" }}>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button 
            variant="outlined" 
            onClick={() => setPaginationModel({ ...paginationModel, pageSize: hostings.length })}
            disabled={paginationModel.pageSize >= hostings.length}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
          >
            Afficher la totalité ({hostings.length})
          </Button>

          <SearchWrapper>
            <SearchIconWrapper><SearchIcon fontSize="small" /></SearchIconWrapper>
            <StyledInputBase 
              placeholder="Rechercher un hébergement..." 
              onChange={(e) => token && getWithFilter(token, "/hosting/filter/", e.target.value, setHostings, () => dispatch(sessionEnd()), setError)}
            />
          </SearchWrapper>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate("/hebergement/ajout")}
            sx={{ 
              ml: 2, borderRadius: "12px", textTransform: "none", fontWeight: 700, px: 3, py: 1,
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.25)",
              "&:hover": { boxShadow: "0 6px 16px rgba(59, 130, 246, 0.35)", transform: "translateY(-1px)" },
              transition: "all 0.2s"
            }}
          >
            Ajouter hébergement
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableRowSelectionOnClick
          slots={{
            toolbar: () => (
              <GridToolbarContainer sx={{ p: 1, gap: 1 }}>
                <GridToolbarColumnsButton sx={{ color: "#64748b" }} />
                <GridToolbarFilterButton sx={{ color: "#64748b" }} />
                <GridToolbarExport sx={{ color: "#64748b" }} />
              </GridToolbarContainer>
            ),
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#f8fafc",
              color: "#64748b",
              borderRadius: "8px",
              fontWeight: 700,
            },
            "& .MuiDataGrid-cell": { borderColor: "#f1f5f9" },
            "& .MuiDataGrid-row:hover": { bgcolor: "#f8fafc" },
          }}
        />
      </Card>

      <AlerteDialog 
        open={open} 
        handleClose={() => setOpen(false)} 
        deleteProject={handleDelete} 
        title="Supprimer l'hébergement ?" 
        contentText="Cette action est définitive." 
      />
      
      <AlerteDialog 
        open={openConfirmCopy} 
        handleClose={() => setOpenConfirmCopy(false)} 
        deleteProject={handleDuplicate} 
        title="Dupliquer l'hébergement ?" 
        contentText="Voulez-vous créer une copie de cet hébergement ?" 
      />
    </Box>
  );
};

export default ListHostings;